//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { Alert } from '@components/atoms/alert/Alert';
import Icon from '@components/atoms/icon/Icon';
import Spinner from '@components/atoms/spinner/Spinner';
import { BodyText, SubtitleText } from '@components/atoms/text/Text';
import Cuid from '@models/Cuid';
import PartialCuid from '@models/PartialCuid';
import { getClientId } from '@redux/selectors/getClientId';
import { useMediaTrackingContext } from '@tracker/mediaTracking/contexts/MediaTrackingContext';
import { MediaTrackingEventName } from '@tracker/mediaTracking/mediaTrackingModel';
import { useSkipRightsMutation } from '@uia/details/hooks/useSkipRightsMutation';
import { UIAFooter } from '@uia/layout/UIAFooter';
import { UIA_RIGHTS_FLOW_TRACKING_IDS } from '@uia/rights/constants/uiaRightsConstants';
import {
  getIsBazaarvoiceNetworkSyndicationFrontendEnabled,
  getIsRenderedWithinAnotherWorkflow,
} from '@uia/rights/selectors/getUIARights';

interface Props {
  handleReturnToRightsOptions: () => void;
  handleSubmit?: () => void;
  onUpdateRights: () => void;
  partialCuid: PartialCuid;
}

export function UIARightsMarkSkippedForm({
  handleReturnToRightsOptions,
  handleSubmit,
  onUpdateRights,
  partialCuid,
}: Props) {
  // TODO: PD-269777 - Remove once UIA React port is complete
  const isRenderedWithinAnotherWorkflow = useSelector(
    getIsRenderedWithinAnotherWorkflow,
  );
  const isBazaarvoiceNetworkSyndicationFrontendEnabled = useSelector(
    getIsBazaarvoiceNetworkSyndicationFrontendEnabled,
  );

  const clientId = useSelector(getClientId);
  const mutation = useSkipRightsMutation({ onUpdateRights });
  const { trackEvent } = useMediaTrackingContext();

  return (
    <OverlayWrapper>
      <FormContainer>
        <SubtitleText>Mark as skipped</SubtitleText>
        <InfoContainer>
          <StyledIcon name='info' />
          <InfoText>
            This image will be used without requesting permission.
          </InfoText>
        </InfoContainer>
        {isBazaarvoiceNetworkSyndicationFrontendEnabled && (
          <StyledAlert type='warning'>
            Content rights must be requested to be eligible for distribution to
            the Visual Syndication Network.
          </StyledAlert>
        )}
        <ApproveContainer>
          <UIAFooter.ActionButton
            isDisabled={mutation.isLoading}
            onClick={() => {
              trackEvent({
                cuid: Cuid.fromPartial(partialCuid, clientId),
                event: MediaTrackingEventName.RightsSkipped,
              });
              void mutation
                .mutateAsync({
                  cuid: partialCuid,
                  clientId,
                })
                .catch((err) => {
                  console.error(err);
                })
                .finally(() => handleSubmit?.());
            }}
            trackingId={
              UIA_RIGHTS_FLOW_TRACKING_IDS.SKIP_RIGHTS_CONFIRM_SKIP_BUTTON
            }
          >
            <ActionButtonContainer>
              Confirm Skip
              {mutation.isLoading && <StyledSpinner />}
            </ActionButtonContainer>
          </UIAFooter.ActionButton>
        </ApproveContainer>
      </FormContainer>
      {!isRenderedWithinAnotherWorkflow && (
        <UIAFooter>
          <UIAFooter.TopSection>
            <StyledText>1 of 1</StyledText>
            <VerticalDivider />
            <UIAFooter.PreviousButton
              onClick={() => handleReturnToRightsOptions()}
              trackingId={
                UIA_RIGHTS_FLOW_TRACKING_IDS.MARK_APPROVED_PREVIOUS_BUTTON
              }
            />
          </UIAFooter.TopSection>
          <UIAFooter.BottomSection>
            <UIAFooter.ActionButton
              isDisabled={true}
              trackingId={
                UIA_RIGHTS_FLOW_TRACKING_IDS.MARK_APPROVED_DONE_BUTTON
              }
            >
              Done
            </UIAFooter.ActionButton>
          </UIAFooter.BottomSection>
        </UIAFooter>
      )}
    </OverlayWrapper>
  );
}

const OverlayWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  width: 100%;
`;

const FormContainer = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[3]};
  `,
);

const VerticalDivider = styled.div(
  ({ theme }) => `
    height: ${theme.spacings[6]};
    border-left: ${theme.borderWidths[1]} solid ${theme.colors.gray1};
  `,
);

const StyledAlert = styled(Alert)(
  ({ theme }) => `
  margin: ${theme.spacings[3]} 0;
`,
);

const ActionButtonContainer = styled.div`
  display: flex;
  align-items: center;
`;

const InfoContainer = styled(BodyText)(
  ({ theme }) => `
  display: flex;
  align-items: center;
  margin-top: ${theme.spacings[3]};
`,
);

const InfoText = styled.span(
  ({ theme }) => `
    margin-left: ${theme.spacings[1]};
  `,
);

const StyledText = styled(BodyText)(
  ({ theme }) => `
  margin: 0 ${theme.spacings[3]};
  color: ${theme.colors.teal1};
`,
);

const StyledSpinner = styled(Spinner)(
  ({ theme }) => `
  width: ${theme.spacings[3]};
  height: ${theme.spacings[3]};
`,
);

const ApproveContainer = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: center;
    margin-top: ${theme.spacings[5]};
  `,
);

const StyledIcon = styled(Icon)(
  ({ theme }) => `
    color: ${theme.colors.aliases.bodySecondary};
  `,
);
